import { InputControl, TextareaControl } from "@shared/components/form";
import LinkFormControl from "@shared/components/form/LinkFormControl";
import ModalBlank from "@shared/components/modals/ModalBlank";
import { Button } from "@shared/components/ui/Button";
import React, { useState } from "react";
import { GettingStartedSectionData } from "../../types/home.types";

// Simple form values interface
interface FormValues {
  title: string;
  description: string;
  buttonText: string;
  buttonLink: string;
  buttonTarget: string;
}

interface EditGettingStartedModalProps {
  initialData: GettingStartedSectionData;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Partial<GettingStartedSectionData>) => Promise<void>;
  isLoading?: boolean;
}

const EditGettingStartedModal: React.FC<EditGettingStartedModalProps> = ({
  initialData,
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
}) => {
  // Initialize form state
  const [formValues, setFormValues] = useState<FormValues>({
    title: initialData?.title || "",
    description: initialData?.description || "",
    buttonText: initialData?.buttonText || "",
    buttonLink: initialData?.buttonLink || "",
    buttonTarget: initialData?.buttonTarget || "_self",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Handle input changes
  const handleChange = (field: keyof FormValues, value: string) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error for this field when user types
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  // Handle link form changes
  const handleLinkChange = (
    field: "text" | "link" | "target",
    value: string
  ) => {
    // Map LinkFormControl fields to form fields
    const fieldMap: Record<string, keyof FormValues> = {
      text: "buttonText",
      link: "buttonLink",
      target: "buttonTarget",
    };

    handleChange(fieldMap[field], value);
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formValues.title.trim()) {
      newErrors.title = "Title is required";
    }

    if (!formValues.description.trim()) {
      newErrors.description = "Description is required";
    }

    if (!formValues.buttonText.trim()) {
      newErrors.buttonText = "Button text is required";
    }

    if (!formValues.buttonLink.trim()) {
      newErrors.buttonLink = "Button link is required";
    }

    // Target is optional but should be validated if provided
    if (
      formValues.buttonTarget &&
      !["_self", "_blank", "_parent", "_top", "internal"].includes(
        formValues.buttonTarget
      )
    ) {
      newErrors.buttonTarget = "Invalid target value";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(formValues);
      onClose();
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  return (
    <ModalBlank
      id="edit-getting-started-modal"
      modalOpen={isOpen}
      setModalOpen={onClose}
      showCloseButton={true}
      className="max-w-[500px] p-6"
    >
      <div className="mb-4">
        <h2 className="text-lg font-semibold">
          Edit "Getting Started" Section
        </h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <InputControl
          id="title"
          label="Title"
          value={formValues.title}
          onChange={(e) => handleChange("title", e.target.value)}
          placeholder="Enter title"
          disabled={isLoading}
          error={errors.title}
          required
        />

        <TextareaControl
          id="description"
          label="Description"
          value={formValues.description}
          onChange={(e) => handleChange("description", e.target.value)}
          placeholder="Enter description"
          disabled={isLoading}
          error={errors.description}
          required
        />

        <LinkFormControl
          idPrefix="button"
          value={{
            text: formValues.buttonText,
            link: formValues.buttonLink,
            target: formValues.buttonTarget,
          }}
          onChange={handleLinkChange}
          disabled={isLoading}
          errors={{
            text: errors.buttonText,
            link: errors.buttonLink,
            target: errors.buttonTarget,
          }}
          required
        />

        <div className="flex justify-end space-x-2 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </form>
    </ModalBlank>
  );
};

export default EditGettingStartedModal;
